import React, { useState } from 'react'
import PropTypes from 'prop-types'

import Button from 'components/Button'
import { Title60, Eyebrow } from 'components/Typography'
import { Container, Spacing50 } from 'styles/sharedStyle'
import theme from 'styles/theme'
import GA4 from 'utils/GA4'

import Card from 'components/Card'
import { FormDropdown, useForm } from 'components/Forms'
import { ListItem, ListWrapper, Section } from './ArticleList.style'
import { fixAudiences } from '../../utils/audiences'

const ArticleList = (props) => {
  const {
    audiences,
    articles,
    eyebrow,
    header,
    loadMore,
    categoryFilter,
    enableImageFadeIn,
    villageName,
    pageTypeComponent,
  } = props
  const ARTICLES_PER_PAGE = 6
  const [articlesToShow, setArticlesToShow] = useState(
    articles.slice(0, ARTICLES_PER_PAGE)
  )
  const [pagesShowing, setPagesShowing] = useState(1)
  const [totalPages, setTotalPages] = useState(
    Math.ceil(articles.length / ARTICLES_PER_PAGE)
  )
  const [currentCategory, setCurrentCategory] = useState('ALL')

  const showNextPage = () => {
    const newPageCount = pagesShowing + 1
    const filteredArticles =
      categoryFilter &&
      articles.filter(({ category }) =>
        currentCategory === 'ALL'
          ? true
          : category.replace(/\s/g, '') === currentCategory
      )
    setArticlesToShow(
      (filteredArticles || articles).slice(0, newPageCount * ARTICLES_PER_PAGE)
    )
    setPagesShowing(newPageCount)
  }

  const { form } = (categoryFilter && useForm({ categoryFilter })) || {
    form: null,
  }

  const handleCategoryChange = (e) => {
    const { value } = e.target
    setCurrentCategory(value)
    const filteredArticles = articles.filter(({ category }) =>
      value === 'ALL' ? true : category.replace(/\s/g, '') === value
    )
    setArticlesToShow(filteredArticles.slice(0, ARTICLES_PER_PAGE))
    setTotalPages(Math.ceil(filteredArticles.length / ARTICLES_PER_PAGE))
    setPagesShowing(1)

    if (pageTypeComponent === 'dine') {
      GA4('category_food_select', {
        navigation_label: value,
        village_name: villageName || 'The Bicester Collection',
      })
    }
  }
  const hasAudiences = !!audiences

  return (
    <Container
      p={`0 ${theme.space[4]}`}
      hasAudience={hasAudiences}
      className={fixAudiences(audiences)}>
      <Spacing50 />
      {(eyebrow || header) && (
        <>
          <Section textAlign="center">
            {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
            <Title60 px={[20]} textAlign="center">
              {header}
            </Title60>
          </Section>
          <Spacing50 />
        </>
      )}
      {categoryFilter && (
        <Container
          maxWidth={['none', null, 340]}
          p={[
            `0 ${theme.space[4]} ${theme.space[4]}`,
            null,
            `0 0 ${theme.space[10]}`,
          ]}>
          <FormDropdown
            value={currentCategory}
            handleChange={(e) => handleCategoryChange(e)}
            {...form.categoryFilter}
            centerLabels
            uppercase
          />
        </Container>
      )}
      <Section>
        <ListWrapper>
          {articlesToShow.map((article, index) => {
            const {
              category,
              image,
              publishDate,
              heading,
              slug,
              pageType,
              village,
              locale,
            } = article
            const storySlug = pageType ? `/${pageType.slug}/` : '/stories/'
            return (
              <ListItem
                key={`${slug}_${index.toString()}`}
                onClick={() =>
                  GA4('grid_combo_click', {
                    component_headline: header,
                    headline: heading,
                    village_name: villageName,
                  })
                }>
                <Card
                  {...{
                    article: {
                      eyebrow: category,
                      url: `${
                        village ? `${village.villageSlug}/` : ''
                      }${locale}${storySlug}${slug}`,
                      heading,
                      publishDate,
                    },
                    image,
                    isSmall: index % 2 > 0,
                    enableImageFadeIn,
                  }}
                />
              </ListItem>
            )
          })}
        </ListWrapper>
      </Section>
      {pagesShowing < totalPages && (
        <>
          <Spacing50 />
          <Section textAlign="center">
            <Button onClick={showNextPage} fixedSize>
              {loadMore}
            </Button>
          </Section>
        </>
      )}
      <Spacing50 />
    </Container>
  )
}

const categoryFilterProps = PropTypes.shape({
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
})

ArticleList.propTypes = {
  audiences: PropTypes.string,
  articles: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object]))
    .isRequired,
  eyebrow: PropTypes.string,
  header: PropTypes.string.isRequired,
  loadMore: PropTypes.string.isRequired,
  categoryFilter: categoryFilterProps,
  enableImageFadeIn: PropTypes.bool,
  villageName: PropTypes.string,
  pageTypeComponent: PropTypes.string,
}

ArticleList.defaultProps = {
  audiences: '',
  eyebrow: null,
  categoryFilter: null,
  enableImageFadeIn: false,
  villageName: '',
  pageTypeComponent: '',
}

export default ArticleList
